import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import IconButton from './IconButton';

/**
 * Modal centré — remplace MUI Dialog / antd Modal.
 */
const Modal = (props: {
    open: boolean;
    onClose: () => void;
    title?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    width?: number;
    /**
     * Garde les enfants montés (masqués) quand le modal est fermé.
     * Utile si le contenu détient son propre état d'overlay
     * (ex. un bouton d'action qui ouvre un FormDrawer portailé).
     */
    keepMounted?: boolean;
}) => {
    const { open, onClose } = props;
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!props.open && !props.keepMounted) return null;
    if (typeof document === 'undefined') return null;

    // Structure identique ouvert/fermé : la réconciliation conserve les enfants
    // montés (keepMounted) — seules les classes/visibilité changent.
    return createPortal(
        <div className={props.open
            ? 'fixed inset-0 z-[1000] flex items-center justify-center p-4'
            : 'hidden'}>
            {props.open && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={props.onClose} />
            )}
            <div
                className="relative bg-[var(--eb-surface)] rounded-2xl shadow-2xl border border-[var(--eb-border)] w-full max-h-[90vh] flex flex-col animate-[eb-modal-in_.2s_ease]"
                style={{ maxWidth: props.width || 520 }}
            >
                {props.title && (
                    <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--eb-border)]">
                        <h2 className="text-base font-bold text-[var(--eb-text)]">{props.title}</h2>
                        <IconButton onClick={props.onClose} size={32}><X size={18} /></IconButton>
                    </div>
                )}
                <div className="flex-1 overflow-y-auto p-5">{props.children}</div>
                {props.footer && (
                    <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--eb-border)]">
                        {props.footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
